import { z } from "zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import useChangeEmail from "@/domains/playerMenu/hooks/useChangeEmail";
import useGetCodeTimer from "@/domains/playerMenu/hooks/useGetCodeTimer";
import PlayerMenuSettingsSafetyConfirmChangingEmail from "@/domains/playerMenu/components/settings/safety/changeEmail/PlayerMenuSettingsSafetyConfirmChangingEmail";

const emailSchema = z.string().email({ message: "Некорректный email" });

type Props = {
  currEmail: string;
  onChangeNewEmail: (value: string) => void;
  next: () => void;
};

export default function PlayerMenuSettingsSafetyConfirmChangingEmailContainer({
  currEmail,
  onChangeNewEmail,
  next,
}: Props) {
  const {
    sendOldCodeHandler,
    sendOldError,
    sendNewCodeHandler,
    sendNewError,
    sendNewIsCompleted,
    sendNewIsPending,
  } = useChangeEmail();
  const [code, setCode] = useState("");
  const [validationErrorCode, setValidationErrorCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [validationErrorNewEmail, setValidationErrorNewEmail] = useState("");

  const { getTimer, timer } = useGetCodeTimer();

  useEffect(() => {
    getTimer({ type: "email", payload: currEmail });
  }, []);

  const onSubmit = useCallback(async () => {
    setValidationErrorCode("");
    if (code.length !== 6) {
      setValidationErrorCode("Длина проверочного кода 6 символов");
      return;
    }
    if (newEmail && code) {
      await getTimer({ type: "email", payload: newEmail });
      await sendNewCodeHandler(newEmail, code);
    }
  }, [sendNewCodeHandler, newEmail, code]);

  const onSendOldCode = useCallback(async () => {
    await getTimer({ type: "email", payload: currEmail });
    await sendOldCodeHandler(currEmail);
  }, [sendOldCodeHandler, currEmail]);

  useEffect(() => {
    if (newEmail) {
      try {
        setValidationErrorNewEmail("");
        emailSchema.parse(newEmail);
      } catch (error) {
        if (error instanceof z.ZodError) {
          setValidationErrorNewEmail(error.errors[0].message);
        }
      }
    } else {
      setValidationErrorNewEmail("");
    }
  }, [newEmail]);

  useEffect(() => {
    if (sendNewIsCompleted && !validationErrorNewEmail && !sendNewError) {
      onChangeNewEmail(newEmail);
      next();
    }
  }, [sendNewIsCompleted, newEmail, onChangeNewEmail, validationErrorNewEmail, sendNewError, next]);

  const error = useMemo(() => {
    return validationErrorCode || sendOldError || sendNewError || validationErrorNewEmail;
  }, [sendOldError, sendNewError, validationErrorNewEmail, validationErrorCode]);

  return (
    <PlayerMenuSettingsSafetyConfirmChangingEmail
      address={currEmail}
      onSubmit={onSubmit}
      code={code}
      onChangeCode={setCode}
      onSendCode={onSendOldCode}
      pending={sendNewIsPending}
      sendIsPending={false}
      email={newEmail}
      onChangeEmail={setNewEmail}
      error={error}
      timer={timer}
    />
  );
}
